/**
 * Activity edit modal — full-page editor that bundles dates, actuals, and
 * predecessor/successor relationships into one batch via the cascade
 * workbench endpoints (services/schedule_update/workbench.py).
 *
 * Click Save -> CascadeWorkbenchModal opens layered on top, simulating the
 * batch live (replan semantics — downstream work moves wherever the forward
 * pass says, earlier included). The user can stage recovery edits there
 * (durations, dependency lag/type) before confirming; apply commits
 * everything under one batch_id.
 *
 * Cancel from the workbench leaves the buffer intact so the user can keep
 * editing.
 */
import React from "react";
import {
  EditBufferProvider,
  useEditBuffer,
} from "../../shared/relationshipEditor/EditBuffer/editBuffer";
import RelEditorGrid from "../../shared/relationshipEditor/RelEditorGrid/RelEditorGrid";
import CascadeWorkbenchModal from "../../shared/cascadeWorkbench/CascadeWorkbenchModal/CascadeWorkbenchModal";
import { fetchLogicTree, workbenchApply } from "../../../api/clientV2";
import { apiErrorMessage } from "../../../utils/apiError";
import type {
  EditModalLogicEdit,
  LogicActivity,
  LogicTreeNode,
  TaktColorEntry,
  WorkbenchBody,
  WorkbenchResult,
  WorkbenchSourceInput,
} from "../../../models";
import { nextWorkday, prevWorkday } from "../../../utils/workdays";
import { formatDateNumeric, statusLabel } from "../../../utils/format";
import { constraintAxis } from "../../../utils/p6";
import ConstraintBadge from "../../shared/ConstraintBadge/ConstraintBadge";
import { ActivityStatus } from "../../../utils/p6";
import "./EditEntryModal.css";

interface Props {
  scheduleId: number;
  activityId: number;
  /** Display label for fallback header while focal loads. */
  workCode: string;
  label: string;
  sectionName: string;
  taktB: string;
  taktC: string;
  isCritical?: boolean;
  /** Project workdays used by the +/-1 and shift adjusters. */
  workdays: string[];
  /** Lookups used by the cascade preview modal (passed through). */
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  sectionMap: Map<number, string>;
  zoneMap: Map<number, string>;
  onClose: () => void;
  /** Fired after the workbench apply succeeds, so parent can refresh the
   *  takt board. */
  onApplied: (response: WorkbenchResult) => void;
}

const EditEntryModal: React.FC<Props> = (props) => (
  <EditBufferProvider
    scheduleId={props.scheduleId}
    scopeKey={`takt-modal-${props.activityId}`}
  >
    <EditEntryModalInner {...props} />
  </EditBufferProvider>
);

const EditEntryModalInner: React.FC<Props> = ({
  scheduleId,
  activityId,
  workCode,
  label,
  sectionName,
  taktB,
  taktC,
  isCritical,
  workdays,
  colors,
  activityWorkCodeMap,
  sectionMap,
  zoneMap,
  onClose,
  onApplied,
}) => {
  const { state, discardAll } = useEditBuffer();

  const [focal, setFocal] = React.useState<LogicActivity | null>(null);
  const [activityById, setActivityById] = React.useState<Map<number, LogicActivity>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // initialActual* snapshots the prefilled values so hasAnyChange / Save
  // don't fire a no-op mark_started just from opening the modal on an
  // already-Active activity.
  const [newStart, setNewStart] = React.useState<string>("");
  const [newFinish, setNewFinish] = React.useState<string>("");
  const [actualStart, setActualStart] = React.useState<string>("");
  const [actualFinish, setActualFinish] = React.useState<string>("");
  const [initialActualStart, setInitialActualStart] = React.useState<string>("");
  const [initialActualFinish, setInitialActualFinish] = React.useState<string>("");

  // Save flow — the staged batch the workbench simulates and applies.
  // Frozen at "Save & Review" so the workbench gets stable inputs.
  const [workbenchInput, setWorkbenchInput] = React.useState<{
    source: WorkbenchSourceInput;
    logicEdits: EditModalLogicEdit[];
  } | null>(null);

  // Fetch the logic tree once on open. We need the whole activity index
  // for the predecessor autocomplete, and the focal's pred/succ edges for
  // the editor grid.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLogicTree(scheduleId)
      .then((tree) => {
        if (cancelled) return;
        const map = new Map<number, LogicActivity>();
        const walk = (node: LogicTreeNode) => {
          for (const a of node.activities) map.set(a.id, a);
          for (const c of node.children) walk(c);
        };
        walk(tree);
        const f = map.get(activityId);
        if (!f) {
          setError(`Activity ${activityId} not found in this schedule`);
          setLoading(false);
          return;
        }
        setFocal(f);
        setActivityById(map);
        // Seed scalar inputs from focal's current state
        // Planned Dates show the PLANNED (retained) start/finish — restart_date,
        // not the actual start. Actuals show the real start/finish, straight off
        // the activity (no reverse-engineering from display dates).
        setNewStart(toDateInput(f.planned_start));
        setNewFinish(toDateInput(f.planned_finish));
        const aStart = toDateInput(f.actual_start);
        const aFinish = toDateInput(f.actual_finish);
        setActualStart(aStart);
        setActualFinish(aFinish);
        setInitialActualStart(aStart);
        setInitialActualFinish(aFinish);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, "Failed to load activity"));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, activityId]);

  const isComplete = focal?.status === ActivityStatus.Complete;
  const isActive = focal?.status === ActivityStatus.Active;
  const isNotStarted = focal?.status === ActivityStatus.NotStart;

  // Setting an actual_finish marks the activity Complete on save, which the
  // backend NULLs restart_date/reend_date for. Disable the planned-date
  // inputs while actual_finish is set so the user can't make edits that
  // would just be discarded.
  const willComplete = actualFinish.trim().length > 0;
  // The remaining early start is locked once active: the cascade engine pins an
  // active source's start to its stored restart_date (workbench `_clamped_seed`),
  // so an edit here can't take effect. Re-plan active work via the Finish, or
  // Clear the actual start to revert it to not-started first.
  const plannedStartLocked = isActive || isComplete || willComplete;
  const plannedFinishLocked = isComplete || willComplete;

  // Scheduled (planned) dates in YYYY-MM-DD form for the prefill checkboxes
  // and date-input value comparison.
  const scheduledStart = focal ? toDateInput(focal.planned_start) : "";
  const scheduledFinish = focal ? toDateInput(focal.planned_finish) : "";

  // A P6 date constraint sits under the field it acts on (Start or Finish).
  const cstrAxis = constraintAxis(focal?.cstr_type);
  const constraintRow = focal && (
    <div className="edit-entry-cstr">
      <span className="edit-entry-cstr-label">Constraint</span>
      <ConstraintBadge
        cstrType={focal.cstr_type}
        cstrDate={focal.cstr_date}
        startDate={focal.start}
        finishDate={focal.finish}
      />
    </div>
  );

  // Actual finish is disabled until actual_start has a value. Keyed on the
  // CURRENT input (not the activity's status) so clearing the actual start —
  // which reverts it to not-started — also clears any staged actual finish (the
  // effect below), instead of staging the contradictory clear+finish pair the
  // backend rejects.
  const canSetActualFinish = actualStart.trim().length > 0;

  // Invariant: actual_finish requires actual_start. If the user clears
  // actual_start (e.g. by unchecking the prefill), drop actual_finish too —
  // otherwise Save would be enabled with an invalid combo that the backend
  // rejects.
  React.useEffect(() => {
    if (!canSetActualFinish && actualFinish) {
      setActualFinish("");
    }
  }, [canSetActualFinish, actualFinish]);

  /** The frozen change-under-review the workbench simulates and applies. */
  const buildWorkbenchInput = React.useCallback(() => {
    if (!focal) throw new Error("focal not loaded");
    const logic_edits: EditModalLogicEdit[] = state.edits.map((e) => {
      if (e.kind === "add") {
        return {
          op: "add",
          succ_id: e.succ_id,
          pred_id: e.pred_id,
          pred_type: e.pred_type,
          lag_hours: e.lag_hours,
        };
      }
      if (e.kind === "remove") {
        return { op: "remove", edge_id: e.edge_id };
      }
      return {
        op: "update",
        edge_id: e.edge_id,
        pred_type: e.pred_type,
        lag_hours: e.lag_hours,
      };
    });

    const source: WorkbenchSourceInput = { activity_id: activityId };

    const startChanged =
      isNotStarted && newStart && newStart !== toDateInput(focal.planned_start);
    const finishChanged =
      !isComplete && newFinish && newFinish !== toDateInput(focal.planned_finish);
    if (startChanged) source.new_start = newStart;
    if (finishChanged) source.new_end = newFinish;

    const startChangedActual = actualStart !== initialActualStart;
    const finishChangedActual = actualFinish !== initialActualFinish;
    if (startChangedActual || finishChangedActual) {
      source.actuals = {};
      if (startChangedActual && actualStart) source.actuals.actual_start = actualStart;
      // Cleared a previously-set actual start -> revert to not-started.
      // (startChangedActual && !actualStart implies initialActualStart was set.)
      else if (startChangedActual && !actualStart)
        source.actuals.clear_actual_start = true;
      if (finishChangedActual && actualFinish) source.actuals.actual_finish = actualFinish;
    }

    return { source, logicEdits: logic_edits };
  }, [focal, activityId, state.edits, newStart, newFinish, actualStart, actualFinish, initialActualStart, initialActualFinish, isNotStarted, isComplete]);

  const hasAnyChange = React.useMemo(() => {
    if (!focal) return false;
    if (state.edits.length > 0) return true;
    if (actualStart !== initialActualStart) return true;
    if (actualFinish !== initialActualFinish) return true;
    if (isNotStarted && newStart && newStart !== toDateInput(focal.planned_start)) return true;
    if (!isComplete && newFinish && newFinish !== toDateInput(focal.planned_finish)) return true;
    return false;
  }, [focal, state.edits, newStart, newFinish, actualStart, actualFinish, initialActualStart, initialActualFinish, isNotStarted, isComplete]);

  const validateClientSide = React.useCallback((): string | null => {
    if (actualStart && actualFinish && actualFinish < actualStart) {
      return "Actual finish cannot be before actual start";
    }
    if (newStart && newFinish && newFinish < newStart) {
      return "Planned finish cannot be before planned start";
    }
    return null;
  }, [actualStart, actualFinish, newStart, newFinish]);

  const handleSave = () => {
    if (!focal) return;
    const clientError = validateClientSide();
    if (clientError) {
      setError(clientError);
      return;
    }
    setError(null);
    setWorkbenchInput(buildWorkbenchInput());
  };

  const handleWorkbenchApply = React.useCallback(
    (body: WorkbenchBody) => workbenchApply(scheduleId, body),
    [scheduleId],
  );

  const handleWorkbenchApplied = React.useCallback(
    (result: WorkbenchResult) => {
      onApplied(result);
      discardAll();
      onClose();
    },
    [onApplied, discardAll, onClose],
  );

  const handleCancelPreview = () => {
    setWorkbenchInput(null);
  };

  const handleClose = () => {
    discardAll();
    onClose();
  };

  if (error && !focal) {
    return (
      <div className="edit-entry-overlay" onClick={handleClose}>
        <div className="edit-entry-modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="edit-entry-error">{error}</div>
          <button className="edit-entry-cancel" onClick={handleClose}>Close</button>
        </div>
      </div>
    );
  }

  const locationSuffix = [taktB, taktC].filter(Boolean).join(" - ");

  return (
    <>
      <div className="edit-entry-overlay" onClick={handleClose}>
        <div className="edit-entry-modal-large" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="edit-entry-header">
            <div>
              <div className="edit-entry-title">
                <span
                  className="rel-editor-activity-badge edit-entry-title-badge"
                  style={
                    colors[workCode]?.fill
                      ? { backgroundColor: colors[workCode].fill }
                      : undefined
                  }
                >
                  {workCode}
                </span>
                {focal?.task_name || label}
                {isCritical && <span className="cp-badge">CP</span>}
              </div>
              <div className="edit-entry-section">
                {[label, locationSuffix, sectionName].filter(Boolean).join(" · ")}
              </div>
              {focal && (
                <div className="edit-entry-stats">
                  <span className="edit-entry-stat-status">
                    <StatusDot status={focal.status} /> {statusLabel(focal.status)}
                  </span>
                  {focal.remaining_duration !== null && (
                    <span className="edit-entry-stat">
                      <b>{+focal.remaining_duration.toFixed(1)}d</b> Rem Duration
                    </span>
                  )}
                  {focal.total_float !== null && (
                    <span className="edit-entry-stat">
                      <b>{focal.total_float}d</b> float
                    </span>
                  )}
                </div>
              )}
            </div>
            <button className="edit-entry-close-x" onClick={handleClose} aria-label="Close">
              &times;
            </button>
          </div>

          {loading && <div className="edit-entry-loading">Loading...</div>}

          {focal && !loading && (
            <div className="edit-entry-body">
              {/* LEFT COLUMN: scalars */}
              <div className="edit-entry-left">
                <Section title="Planned Dates">
                 <div className="edit-entry-dates-group">
                  <div className="edit-entry-dates">
                    <div className="edit-entry-field">
                      <div className="edit-entry-field-header">
                        <label>Start</label>
                        <div className="edit-entry-adjust-btns">
                          <button
                            className="btn-adjust btn-adjust-sm"
                            onClick={() => setNewStart((s) => prevWorkday(s, workdays))}
                            disabled={plannedStartLocked}
                          >
                            &minus;1
                          </button>
                          <button
                            className="btn-adjust btn-adjust-sm"
                            onClick={() =>
                              setNewStart((s) => {
                                const nxt = nextWorkday(s, workdays);
                                return nxt <= newFinish ? nxt : s;
                              })
                            }
                            disabled={plannedStartLocked}
                          >
                            +1
                          </button>
                        </div>
                      </div>
                      <input
                        type="date"
                        value={newStart}
                        disabled={plannedStartLocked}
                        onChange={(e) => setNewStart(e.target.value)}
                      />
                      {plannedStartLocked && !willComplete && (
                        <span className="edit-entry-hint">
                          {isActive ? "started — re-plan via Finish" : "completed"}
                        </span>
                      )}
                      {cstrAxis === "start" && constraintRow}
                    </div>

                    <div className="edit-entry-field">
                      <div className="edit-entry-field-header">
                        <label>Finish</label>
                        <div className="edit-entry-adjust-btns">
                          <button
                            className="btn-adjust btn-adjust-sm"
                            onClick={() =>
                              setNewFinish((f) => {
                                const prv = prevWorkday(f, workdays);
                                return prv >= newStart ? prv : f;
                              })
                            }
                            disabled={plannedFinishLocked}
                          >
                            &minus;1
                          </button>
                          <button
                            className="btn-adjust btn-adjust-sm"
                            onClick={() => setNewFinish((f) => nextWorkday(f, workdays))}
                            disabled={plannedFinishLocked}
                          >
                            +1
                          </button>
                        </div>
                      </div>
                      <input
                        type="date"
                        value={newFinish}
                        disabled={plannedFinishLocked}
                        onChange={(e) => setNewFinish(e.target.value)}
                      />
                      {isComplete && (
                        <span className="edit-entry-hint">completed — finish is locked</span>
                      )}
                      {cstrAxis === "finish" && constraintRow}
                    </div>
                  </div>

                  {/* Shift both planned dates together — centered under the
                   * inputs so it visually anchors between Start and Finish.
                   * Hidden when planned dates are locked (Complete or about
                   * to be marked Complete). */}
                  {!plannedFinishLocked && (
                    <div className="edit-entry-shift">
                      <span className="edit-entry-adjust-label">Shift</span>
                      <div className="edit-entry-adjust-btns">
                        <button
                          className="btn-adjust"
                          onClick={() => {
                            if (!isActive) setNewStart((s) => prevWorkday(s, workdays));
                            setNewFinish((f) => prevWorkday(f, workdays));
                          }}
                        >
                          &larr;
                        </button>
                        <button
                          className="btn-adjust"
                          onClick={() => {
                            if (!isActive) setNewStart((s) => nextWorkday(s, workdays));
                            setNewFinish((f) => nextWorkday(f, workdays));
                          }}
                        >
                          &rarr;
                        </button>
                      </div>
                    </div>
                  )}
                 </div>
                </Section>

                <Section title="Actuals">
                 <div className="edit-entry-dates-group">
                  <div className="edit-entry-dates">
                    <div className="edit-entry-field">
                      <div className="edit-entry-field-header">
                        <label>Actual Start</label>
                        {!isComplete && actualStart && (
                          <button
                            type="button"
                            className="btn-adjust btn-adjust-sm"
                            onClick={() => setActualStart("")}
                            title="Clear the actual start — reverts the activity to not-started"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <input
                        type="date"
                        value={actualStart}
                        disabled={isComplete}
                        onChange={(e) => setActualStart(e.target.value)}
                      />
                      {scheduledStart && !isComplete && !actualStart && (
                        <label className="edit-entry-prefill">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={(e) =>
                              setActualStart(e.target.checked ? scheduledStart : "")
                            }
                          />
                          <span>
                            Started on scheduled date ({formatDateNumeric(scheduledStart)})
                          </span>
                        </label>
                      )}
                      {initialActualStart && !actualStart && (
                        <span className="edit-entry-hint">
                          ↩ clears the start — reverts to not started on save
                        </span>
                      )}
                    </div>

                    <div className="edit-entry-field">
                      <label>Actual Finish</label>
                      <input
                        type="date"
                        value={actualFinish}
                        disabled={!canSetActualFinish || isComplete}
                        onChange={(e) => setActualFinish(e.target.value)}
                      />
                      {scheduledFinish && canSetActualFinish && !isComplete && !actualFinish && (
                        <label className="edit-entry-prefill">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={(e) =>
                              setActualFinish(e.target.checked ? scheduledFinish : "")
                            }
                          />
                          <span>
                            Finished on scheduled date ({formatDateNumeric(scheduledFinish)})
                          </span>
                        </label>
                      )}
                      {isComplete && (
                        <span className="edit-entry-hint">already completed</span>
                      )}
                    </div>
                  </div>
                 </div>
                </Section>
              </div>

              {/* RIGHT COLUMN: relationships */}
              <div className="edit-entry-right">
                <Section title={`Predecessors (${focal.predecessors.length})`}>
                  <RelEditorGrid
                    focal={focal}
                    direction="predecessors"
                    activityById={activityById}
                    colors={colors}
                  />
                </Section>

                <Section title={`Successors (${focal.successors.length})`}>
                  <RelEditorGrid
                    focal={focal}
                    direction="successors"
                    activityById={activityById}
                    colors={colors}
                  />
                </Section>
              </div>
            </div>
          )}

          {/* Footer */}
          {error && focal && <div className="edit-entry-error">{error}</div>}
          <div className="edit-entry-actions">
            <button className="edit-entry-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="edit-entry-save"
              onClick={handleSave}
              disabled={!hasAnyChange || loading}
            >
              Save &amp; Review
            </button>
          </div>
        </div>
      </div>

      {workbenchInput && (
        <CascadeWorkbenchModal
          scheduleId={scheduleId}
          source={workbenchInput.source}
          initialLogicEdits={workbenchInput.logicEdits}
          colors={colors}
          activityWorkCodeMap={activityWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onCancel={handleCancelPreview}
          onApply={handleWorkbenchApply}
          onApplied={handleWorkbenchApplied}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="edit-entry-sec">
    <div className="edit-entry-sec-title">{title}</div>
    {children}
  </div>
);

const STATUS_DOT_CLASS: Record<string, string> = {
  TK_Complete: "status-complete",
  TK_Active: "status-active",
  TK_NotStart: "status-notstart",
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`status-dot ${STATUS_DOT_CLASS[status] ?? "status-notstart"}`}
    aria-label={statusLabel(status)}
  />
);

function toDateInput(iso: string | null): string {
  // Accepts "YYYY-MM-DD" or "YYYY-MM-DD HH:MM"; date inputs need "YYYY-MM-DD".
  return iso ? iso.slice(0, 10) : "";
}

export default EditEntryModal;
